import React, { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Container, EllipseGroupWrapper, SummaryContainer } from './styles'
import { TimelineTooltipEnclosed } from '../../timelineTooltipEnclosed'
import { EllipseButton } from '../../ellipseButton'
import { IHealthProceduresOmics } from '~/interfaces'
import { HealthProcedureVariantProps } from '~/constants'
import { formatHealthProceduresTooltipDescriptionWithDate, preventTooltipOfBigDescriptionsIn } from '~/utils'

// TODO: Remover após alterar as interfaces de IHealthProcedures para IMedicalEvents
interface IMedicalEvents extends IHealthProceduresOmics {}

interface SummaryTooltipProps {
  medicalEvents: IMedicalEvents[]
  isShowing: boolean
  portalX?: number
  portalY?: number
  onHide: () => void
  onClick: (medicalEvents: IMedicalEvents[]) => void
}

const SummaryTooltip = ({
  medicalEvents,
  isShowing,
  portalX = 0,
  portalY = 0,
  onHide,
  onClick
}: SummaryTooltipProps): JSX.Element => {
  if (!isShowing || (!portalX && !portalY)) {
    return <></>
  }

  const isPositionAboveOneThirdScreen = portalY > window.innerHeight / 3
  const groupPerType = (medicalEvents: IMedicalEvents[]): IMedicalEvents[][] => {
    const groupedMedicalEventsPerType = medicalEvents.reduce<IMedicalEvents[][]>((acc, medicalEvent) => {
      const type = medicalEvent.type
      const existentType = acc.findIndex(me => me[0].type === type)

      if (existentType >= 0) {
        acc[existentType] = [...acc[existentType], medicalEvent]
        return acc
      }

      return [...acc, [medicalEvent]]
    }, [])

    return groupedMedicalEventsPerType
  }

  const tooltipPosition = {
    top: isPositionAboveOneThirdScreen ? portalY : undefined,
    bottom: isPositionAboveOneThirdScreen ? undefined : window.innerHeight - portalY,
    left: portalX
  }

  const summary = () => {
    return (
      <SummaryContainer data-testid="ellipse-summary" {...tooltipPosition} onMouseLeave={onHide}>
        {groupPerType(medicalEvents).map(groupMedicalEvent => {
          const ellipseVariant = HealthProcedureVariantProps[groupMedicalEvent[0].type]
          const descriptions: string[] = formatHealthProceduresTooltipDescriptionWithDate(groupMedicalEvent)
          const tooltipDescriptionMultiline = preventTooltipOfBigDescriptionsIn(descriptions)

          return (
            <EllipseGroupWrapper
              key={`${groupMedicalEvent[0].id}-group`}
              onClick={event => {
                event.stopPropagation()
                onClick(groupMedicalEvent)
              }}
            >
              <TimelineTooltipEnclosed description={tooltipDescriptionMultiline}>
                <EllipseButton
                  color={ellipseVariant.color}
                  quantity={groupMedicalEvent.length > 1 ? groupMedicalEvent.length : undefined}
                  iconName={ellipseVariant.iconName}
                  alert={groupMedicalEvent.some(medicalEvent => medicalEvent.isAltered)}
                />
              </TimelineTooltipEnclosed>
            </EllipseGroupWrapper>
          )
        })}
      </SummaryContainer>
    )
  }

  return createPortal(summary(), document.body)
}

interface GroupSummaryTooltipProps {
  children: React.ReactNode
  medicalEvents: IMedicalEvents[]
  onClick: (medicalEvents: IMedicalEvents[]) => void
}

export const GroupSummaryTooltip = ({ children, medicalEvents, onClick }: GroupSummaryTooltipProps): JSX.Element => {
  let timeout: NodeJS.Timeout
  const childrenRef = useRef<HTMLDivElement>(null)
  const [isShowingSummary, setIsShowingSummary] = useState(false)
  const showSummary = () => setIsShowingSummary(true)
  const hideSummary = () => {
    clearTimeout(timeout)
    timeout = setTimeout(() => setIsShowingSummary(false), 150)
  }

  const tooltipXPosition = childrenRef.current?.getBoundingClientRect().x
  const tooltipYPosition = childrenRef.current?.getBoundingClientRect().y

  const onMouseMove = () => {
    clearTimeout(timeout)
  }

  return (
    <Container
      ref={childrenRef}
      onMouseOver={showSummary}
      onMouseMove={onMouseMove}
      onMouseLeave={hideSummary}
      data-testid="summary-ellipse-trigger"
    >
      {children}
      <SummaryTooltip
        medicalEvents={medicalEvents}
        isShowing={isShowingSummary}
        portalX={tooltipXPosition}
        portalY={tooltipYPosition}
        onHide={hideSummary}
        onClick={onClick}
      />
    </Container>
  )
}
